/**
 * FocusNavigation implementation manages focus navigation mode state through signals.
 * It initializes from sessionStorage and persists changes, providing reactive state
 * that components can subscribe to. The implementation follows the dependency
 * management pattern with a factory function that accepts dependencies.
 */
import { signal } from '@preact/signals-react'
import type { FocusNavigation, FocusNavigationDeps, FocusArea } from './focusNavigation.types'

const DEFAULT_STORAGE_KEY = 'merels:focus-navigation-active'

function getBrowserStorage(): { getItem(key: string): string | null; setItem(key: string, value: string): void } {
  if (typeof window === 'undefined') {
    // Server-side: return no-op storage
    return {
      getItem: () => null,
      setItem: () => {},
    }
  }
  return window.sessionStorage
}

export function makeFocusNavigation(deps: FocusNavigationDeps = {}): FocusNavigation {
  const { storageKey = DEFAULT_STORAGE_KEY, storage = getBrowserStorage() } = deps

  // Initialize from storage
  const initialIsActive = (() => {
    try {
      const stored = storage.getItem(storageKey)
      return stored === 'true'
    } catch {
      return false
    }
  })()

  const focusArea = signal<FocusArea>('content')
  const isActive = signal<boolean>(initialIsActive)

  // Helper to persist isActive to storage
  const persistIsActive = (value: boolean) => {
    try {
      storage.setItem(storageKey, String(value))
    } catch (error) {
      // Ignore storage errors (e.g., quota exceeded, private browsing)
      console.warn('Failed to save focus navigation state to storage:', error)
    }
  }

  function setFocusArea(area: FocusArea): void {
    focusArea.value = area
  }

  function enable(): void {
    isActive.value = true
    persistIsActive(true)
  }

  function disable(): void {
    isActive.value = false
    persistIsActive(false)
  }

  function enableFromControllerUsage(
    input: { deviceId: string; rawEvent: string },
    isSharedScreen: boolean
  ): void {
    if (isActive.value) return

    const isKeyboard = input.deviceId === 'K'
    // Shared-screen policy: keyboard does not represent a mate, so don't enable focus-nav
    // based on general keyboard navigation. Allow keyboard Menu as a special-case.
    if (isSharedScreen && isKeyboard && input.rawEvent !== 'M key') {
      return
    }

    enable()
  }

  return {
    focusArea,
    isActive,
    setFocusArea,
    enable,
    disable,
    enableFromControllerUsage,
  }
}
